import React, { useCallback, useEffect, useRef, useState } from 'react';

import { listAdmissions } from '../../services/api/admissionApi';
import { getIpdDepositsReport } from '../../services/api/ipdReportsApi';
import { ADMISSION_STATUS_LABELS, PAYMENT_MODE_LABELS, SETTLEMENT_WINDOW_STATUSES } from '../../constants/admission';
import { formatPaise, todayYmd } from '../../utils/money';
import { backdatedDischargeTag } from './backdatedCompletions';
import { buildDepositHeld } from './depositBreakdown';

const SETTLEMENT_STATUSES = Array.from(SETTLEMENT_WINDOW_STATUSES);

const overlayCls = 'fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4';
const panelCls = 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl w-full max-w-4xl max-h-[92vh] overflow-y-auto';
const btnSecondary = 'px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm';
const btnRetry = 'px-3 py-1.5 rounded-lg text-[12px] font-semibold border border-red-400 dark:border-red-600 bg-white dark:bg-red-900/40 text-red-800 dark:text-red-100 hover:bg-red-100 dark:hover:bg-red-800 flex-none';
const headerRowCls = 'flex gap-2.5 px-2 py-1.5 text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700';
const dataRowCls = 'flex gap-2.5 px-2 py-2 text-[13px] border-b border-slate-100 dark:border-slate-800';

/**
 * DepositBreakdownModal (FE-1, S2 drill-downs) — ONE modal shell shared by
 * the two dashboard-tile drill-downs (`variant="held"` / `variant="today"`).
 * Fetches ON MOUNT (i.e. on click, since the caller mounts this component
 * only when the tile is clicked — never on dashboard load), single-flight
 * guarded (`fetchedRef`), same pattern as `IpdDepositsReportDialog`.
 *
 * - `variant="held"`: `listAdmissions` over the 3 SETTLEMENT_WINDOW_STATUSES
 *   (Promise.all, on-demand) → `buildDepositHeld` (the ONE home of the held
 *   rule, shared with the reconcile-invariant test) → per-patient rows.
 *   Subtotal is `buildDepositHeld(...).total_paise` — NOT re-summed here.
 * - `variant="today"`: `getIpdDepositsReport()` (JSON, same source that
 *   feeds the dashboard's `collected_today_paise` tile and the deposits
 *   PDF) → rows carry `primary_doctor_name` + `status` (BE-2, C2 frozen
 *   contract) rendered via the shared `ADMISSION_STATUS_LABELS` map.
 *   Footer total is `report.day_total_paise` — reconciles by construction.
 */
export default function DepositBreakdownModal({ variant, onClose }) {
  const [state, setState] = useState('loading'); // 'loading' | 'error' | 'data'
  const [error, setError] = useState(null);
  const [heldRows, setHeldRows] = useState([]);
  const [heldTotal, setHeldTotal] = useState(0);
  const [todayReport, setTodayReport] = useState(null);
  const fetchedRef = useRef(false);

  const loadHeld = useCallback(async () => {
    setState('loading');
    setError(null);
    const results = await Promise.all(
      SETTLEMENT_STATUSES.map((status) => listAdmissions({ status })),
    );
    const failed = results.find((r) => !r.success);
    if (failed) {
      setError(failed.error || "Couldn't load deposit-held patients.");
      setState('error');
      return;
    }
    const rows = results.flatMap((r) => r.data.admissions);
    const { rows: held, total_paise } = buildDepositHeld(rows);
    setHeldRows(held);
    setHeldTotal(total_paise);
    setState('data');
  }, []);

  const loadToday = useCallback(async () => {
    setState('loading');
    setError(null);
    const res = await getIpdDepositsReport();
    if (!res.success) {
      setError(res.error || "Couldn't load today's deposits.");
      setState('error');
      return;
    }
    setTodayReport(res.data);
    setState('data');
  }, []);

  const load = variant === 'held' ? loadHeld : loadToday;

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    load();
    // Fires exactly once per modal mount (guarded above) — the modal itself
    // only ever mounts on a tile click (see AdmissionDashboardPanel), so
    // this is the on-click fetch, never a dashboard-load fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // COPY: title/error labels pending Business ratification; subtitle/empty/column-header RATIFIED (held variant).
  const title = variant === 'held' ? 'Deposit held — patient breakdown' : 'Deposit collected today';
  const subtitle = variant === 'held'
    ? 'Admitted patients — including those being discharged or in billing clearance — who currently hold a deposit.'
    : todayYmd();
  const emptyText = variant === 'held'
    ? 'No patients are holding a deposit right now.'
    : 'No deposits collected today.';
  const errorFallback = variant === 'held'
    ? "Couldn't load deposit-held patients."
    : "Couldn't load today's deposits.";

  // Refund / net figures (today variant) — mirror IpdDepositsReportDialog.
  // Prefer the frozen server field; derive net only for legacy envelopes that
  // predate `net_deposit_paise` (single inline subtraction, no re-summing).
  const refunds = todayReport?.refunds || [];
  const refundsTotalPaise = todayReport?.refunds_total_paise || 0;
  const netDepositPaise = typeof todayReport?.net_deposit_paise === 'number'
    ? todayReport.net_deposit_paise
    : ((todayReport?.day_total_paise || 0) - refundsTotalPaise);

  return (
    <div className={overlayCls} data-testid="deposit-breakdown-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={panelCls} role="dialog" aria-modal="true" aria-label={title}>
        <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-2">
          <div>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
            <span className="text-[12px] text-slate-500 dark:text-slate-400">{subtitle}</span>
          </div>
          <button type="button" onClick={onClose} className="text-slate-500 dark:text-slate-400" aria-label="Close">✕</button>
        </div>

        <div className="p-4">
          {state === 'loading' && (
            <div data-testid="deposit-breakdown-loading">
              {[40, 85, 78].map((w, i) => (
                <div key={i} className="h-3 rounded bg-slate-200 dark:bg-slate-700 animate-pulse mb-2.5" style={{ width: `${w}%` }} />
              ))}
              <div className="text-[12px] text-slate-500 dark:text-slate-400 mt-2">Loading…</div>
            </div>
          )}

          {state === 'error' && (
            <div
              className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-3 flex items-center justify-between gap-3"
              data-testid="deposit-breakdown-error"
            >
              <span>{error || errorFallback}</span>
              <button type="button" onClick={load} className={btnRetry} data-testid="deposit-breakdown-retry">
                Retry
              </button>
            </div>
          )}

          {state === 'data' && variant === 'held' && (
            heldRows.length === 0 ? (
              <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="deposit-breakdown-empty">
                {emptyText}
              </div>
            ) : (
              <>
                <div className={headerRowCls}>
                  <span className="flex-1">Patient</span>
                  <span className="w-40">Doctor</span>
                  <span className="w-36">Status</span>
                  <span className="w-24 text-right">Deposit held</span>
                </div>
                {heldRows.map((row) => (
                  <div key={row.admission_id} className={dataRowCls} data-testid={`deposit-breakdown-row-${row.admission_id}`}>
                    <span className="flex-1 min-w-0 truncate text-slate-900 dark:text-white">{row.patient_name}</span>
                    <span className="w-40 truncate text-slate-600 dark:text-slate-300">{row.primary_doctor_name || '—'}</span>
                    <span className="w-36 text-slate-600 dark:text-slate-300">{ADMISSION_STATUS_LABELS[row.status] || row.status}</span>
                    <span className="w-24 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{formatPaise(row.deposit_total_paise)}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-300 dark:border-slate-600 text-[14px] font-extrabold text-slate-900 dark:text-white">
                  <span>Total held ({heldRows.length} patient{heldRows.length === 1 ? '' : 's'})</span>
                  <span className="tabular-nums" data-testid="deposit-breakdown-total">{formatPaise(heldTotal)}</span>
                </div>
              </>
            )
          )}

          {state === 'data' && variant === 'today' && (
            (todayReport?.rows || []).length === 0 ? (
              <div className="p-6 text-center text-[12.5px] text-slate-500 dark:text-slate-400" data-testid="deposit-breakdown-empty">
                {emptyText}
              </div>
            ) : (
              <>
                <div className={headerRowCls}>
                  <span className="flex-1">Patient</span>
                  <span className="w-32">Ward / Bed</span>
                  <span className="w-24 text-right">Amount</span>
                  <span className="w-20">Method</span>
                  <span className="w-20">Receipt</span>
                  <span className="w-32">Collected by</span>
                  <span className="w-36">Doctor</span>
                  <span className="w-32">Status</span>
                </div>
                {todayReport.rows.map((row) => (
                  <div key={row.admission_id + String(row.receipt_number)} className={dataRowCls} data-testid={`deposit-breakdown-row-${row.admission_id}`}>
                    <span className="flex-1 min-w-0 truncate text-slate-900 dark:text-white">
                      {row.patient_name}
                      {backdatedDischargeTag(row) && <span className="block text-[11px] text-amber-700 dark:text-amber-400" data-testid="backdated-discharge-tag">{backdatedDischargeTag(row)}</span>}
                    </span>
                    <span className="w-32 text-slate-600 dark:text-slate-300">{[row.ward_name, row.bed_label].filter(Boolean).join(' · ') || '—'}</span>
                    <span className="w-24 text-right font-semibold tabular-nums text-slate-900 dark:text-white">{formatPaise(row.amount_paise)}</span>
                    <span className="w-20 text-slate-600 dark:text-slate-300">{PAYMENT_MODE_LABELS[row.payment_mode] || row.payment_mode}</span>
                    <span className="w-20 text-slate-600 dark:text-slate-300">{row.receipt_number ?? '—'}</span>
                    <span className="w-32 truncate text-slate-600 dark:text-slate-300">{row.collected_by_name}</span>
                    <span className="w-36 truncate text-slate-600 dark:text-slate-300">{row.primary_doctor_name || '—'}</span>
                    <span className="w-32 text-slate-600 dark:text-slate-300">{ADMISSION_STATUS_LABELS[row.status] || row.status || '—'}</span>
                  </div>
                ))}

                {refunds.length > 0 && (
                  <div className="mt-5">
                    <div className="text-[12px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400 mb-1.5" data-testid="deposit-breakdown-refunds-header">
                      Refunded today
                    </div>
                    <div className={headerRowCls}>
                      <span className="flex-1">Patient</span>
                      <span className="w-32">Ward / Bed</span>
                      <span className="w-24 text-right">Amount</span>
                      <span className="w-20">Method</span>
                      <span className="w-20">Receipt</span>
                      <span className="w-32">Refunded by</span>
                      <span className="w-36">Doctor</span>
                      <span className="w-32">Status</span>
                    </div>
                    {refunds.map((row, i) => (
                      <div key={`${row.admission_id}-refund-${row.receipt_number ?? i}`} className={dataRowCls} data-testid={`deposit-breakdown-refund-row-${row.admission_id ?? i}`}>
                        <span className="flex-1 min-w-0 truncate text-slate-900 dark:text-white">
                          {row.patient_name}
                          {backdatedDischargeTag(row) && <span className="block text-[11px] text-amber-700 dark:text-amber-400" data-testid="backdated-discharge-tag">{backdatedDischargeTag(row)}</span>}
                        </span>
                        <span className="w-32 text-slate-600 dark:text-slate-300">{[row.ward_name, row.bed_label].filter(Boolean).join(' · ') || '—'}</span>
                        <span className="w-24 text-right font-semibold tabular-nums text-amber-700 dark:text-amber-400">{formatPaise(-row.amount_paise)}</span>
                        <span className="w-20 text-slate-600 dark:text-slate-300">{PAYMENT_MODE_LABELS[row.payment_mode] || row.payment_mode}</span>
                        <span className="w-20 text-slate-600 dark:text-slate-300">{row.receipt_number ?? '—'}</span>
                        <span className="w-32 truncate text-slate-600 dark:text-slate-300">{row.collected_by_name}</span>
                        <span className="w-36 truncate text-slate-600 dark:text-slate-300">{row.primary_doctor_name || '—'}</span>
                        <span className="w-32 text-slate-600 dark:text-slate-300">{ADMISSION_STATUS_LABELS[row.status] || row.status || '—'}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-300 dark:border-slate-600 text-[14px] font-extrabold text-slate-900 dark:text-white">
                  <span>Day total ({todayReport.count} deposit{todayReport.count === 1 ? '' : 's'})</span>
                  <span className="tabular-nums" data-testid="deposit-breakdown-total">{formatPaise(todayReport.day_total_paise)}</span>
                </div>
                {refunds.length > 0 && (
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-amber-300 dark:border-amber-700 text-[14px] font-extrabold text-amber-700 dark:text-amber-400">
                    <span>Deposit refunded ({refunds.length} refund{refunds.length === 1 ? '' : 's'})</span>
                    <span className="tabular-nums" data-testid="deposit-breakdown-refund-total">{formatPaise(-refundsTotalPaise)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center mt-2 pt-2.5 border-t-2 border-slate-800 dark:border-slate-100 text-[15px] font-extrabold text-slate-900 dark:text-white">
                  <span>Today&rsquo;s Net Deposit</span>
                  <span className="tabular-nums" data-testid="deposit-breakdown-net-total">{formatPaise(netDepositPaise)}</span>
                </div>
              </>
            )
          )}
        </div>

        <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex justify-end">
          <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
